import React, { useMemo, useEffect, useState, Suspense, lazy } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import type { Product } from '../types';
import ProductCard from './ProductCard';
import { useAppStore } from '../store/useAppStore';
import { ProductListSkeleton } from './SkeletonLoader';
import { getUserCurrentLocation, fuzzLocation } from '../services/geo';
import { MapPinIcon } from './Icons';

const ProductDetail = lazy(() => import('./ProductDetail'));

interface ProductListProps {
  onEdit: (product: Product) => void;
  onDuplicate: (product: Product) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
}

const ProductList: React.FC<ProductListProps> = ({ 
  onEdit, 
  onDuplicate, 
  onDelete, 
  onToggleStatus 
}) => {
  const { 
    products, 
    fetchProducts, 
    hasMore, 
    page, 
    isInitialLoad,
    seller,
    userLocation,
    setUserLocation,
    resetProducts
  } = useAppStore();

  const [locationPrompted, setLocationPrompted] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  useEffect(() => {
    if (isInitialLoad) {
      fetchProducts(1, true);
    }
  }, [isInitialLoad, fetchProducts]);

  const handleEnableLocation = async () => {
    setIsLocating(true);
    try {
        const pos = await getUserCurrentLocation();
        setUserLocation({
            lat: fuzzLocation(pos.coords.latitude),
            lng: fuzzLocation(pos.coords.longitude)
        });
        setLocationPrompted(true);
        resetProducts();
    } catch (err) {
        console.warn("Location access denied or failed", err);
        alert("Could not access your location. Please check your browser permissions.");
        setLocationPrompted(true);
    } finally {
        setIsLocating(false);
    }
  };

  const loadMore = () => {
    if (!hasMore) return;
    fetchProducts(page + 1);
  };

  const visibleProducts = useMemo(() => {
    return products.filter(p => p.isActive || (seller && p.seller.contact === seller.contact));
  }, [products, seller]);

  if (isInitialLoad) {
    return <ProductListSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Location Bar */}
      {!userLocation && !locationPrompted && (
        <div className="bg-card-bg border border-brand-green-100 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-card transition-all hover:border-brand-green">
            <div className="flex items-center gap-4">
                <div className="bg-brand-green-50 p-3 rounded-full">
                    <MapPinIcon className="w-6 h-6 text-brand-green" />
                </div>
                <div className="text-center sm:text-left">
                    <h3 className="font-black text-brand-brown text-lg">Find Freshness Nearby</h3>
                    <p className="text-sm text-app-text-secondary">Enable GPS to see products sorted by distance from you.</p>
                </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
                <button 
                    onClick={() => setLocationPrompted(true)} 
                    className="flex-1 sm:flex-none text-sm font-bold text-app-text-secondary hover:text-brand-brown px-4 py-2 transition-colors"
                >
                    Dismiss
                </button>
                <button 
                    onClick={handleEnableLocation}
                    disabled={isLocating}
                    className="flex-1 sm:flex-none text-sm font-black bg-btn-primary text-btn-primary-text px-6 py-2.5 rounded-full shadow-card-hover hover:bg-btn-primary-hover transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {isLocating && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {isLocating ? 'Locating...' : 'Enable Location'}
                </button>
            </div>
        </div>
      )}

      {userLocation && (
          <div className="flex items-center justify-between bg-brand-green-50 px-4 py-2 rounded-lg border border-brand-green-100">
              <div className="flex items-center gap-2">
                  <span className="flex h-2 w-2 rounded-full bg-brand-light-green animate-pulse" />
                  <span className="text-xs font-black uppercase text-brand-green tracking-widest flex items-center gap-1">
                      <MapPinIcon className="w-3 h-3" />
                      Showing Nearest First
                  </span>
              </div>
              <button 
                onClick={() => {setUserLocation(null); resetProducts();}} 
                className="text-[10px] font-black text-app-text-secondary hover:text-brand-brown uppercase tracking-tighter"
              >
                Clear Location
              </button>
          </div>
      )}

      {visibleProducts.length === 0 && !hasMore ? (
        <div className="text-center py-16 animate-in fade-in duration-700">
          <div className="text-6xl mb-4">🌱</div>
          <h2 className="text-2xl font-black text-brand-brown">No products yet</h2>
          <p className="text-app-text-secondary mt-2">The marketplace is quiet. Be the first to plant a seed!</p>
        </div>
      ) : (
        <div id="scrollableDiv" className="overflow-visible">
          <InfiniteScroll
            dataLength={visibleProducts.length}
            next={loadMore}
            hasMore={hasMore}
            loader={<ProductListSkeleton count={4} />}
            endMessage={
              visibleProducts.length > 0 ? (
                <p className="text-center text-app-text-secondary py-10 font-bold text-sm uppercase tracking-widest">
                  You've reached the end of the harvest! 🧺
                </p>
              ) : null
            }
            scrollableTarget="scrollableDiv"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 pb-10">
              {visibleProducts.map(product => (
                <ProductCard 
                    key={product.id} 
                    product={product} 
                    onEdit={onEdit}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                    onToggleStatus={onToggleStatus}
                    onViewDetails={(id) => setSelectedProductId(id)}
                />
              ))}
            </div>
          </InfiniteScroll>
        </div>
      )}

      {selectedProductId && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center text-white font-bold backdrop-blur-sm">Loading Details...</div>}>
          <ProductDetail 
            productId={selectedProductId} 
            onClose={() => setSelectedProductId(null)} 
          />
        </Suspense>
      )}
    </div>
  );
};

export default ProductList;
